import { useEffect, useMemo } from 'react';

import type { TransitionProps } from '@mui/material/transitions';
import { useModal } from 'react-modal-hook';

import { FannieHeader } from 'components/Promo/FannieHeader';
import { FannieModal } from 'components/Promo/FannieModal';
import { FreddieHeader } from 'components/Promo/FreddieHeader';
import { FreddieModal } from 'components/Promo/FreddieModal';
import { VOAHeader } from 'components/Promo/VOAHeader';
import { VOAModal } from 'components/Promo/VOAModal';
import { useGetBillingProfile } from 'entities/billing/api/useGetBillingProfile';
import { AccountActivationHeader } from 'features/billing/ui/promo/PromoAccountActivationHeader';
import { SubscriptionHeader } from 'features/billing/ui/promo/PromoSubscriptionHeader';
import { useFeatures } from 'hooks/features';
import { useAuth } from 'shared/lib/auth/useAuth';
import { StorageKeys, localStorageService } from 'shared/lib/storage/storageService';
import { urls } from 'shared/urls';

export enum Promo {
  FREDDIE_POPUP = 'freddie-popup',
  FREDDIE_HEADER = 'freddie-header',
  FANNIE_POPUP = 'fannie-popup',
  FANNIE_HEADER = 'fannie-header',
  VOA_POPUP = 'voa-popup',
  VOA_HEADER = 'voa-header',
  VOA_FANNIE_POPUP = 'voa-fannie-popup',
  VOA_FANNIE_HEADER = 'voa-fannie-header',
  SUBSCRIPTION_HEADER = 'subscription-header',
  ACCOUNT_ACTIVATION_HEADER = 'account-activation-header',
}

// Convert Promo to StorageKeys for type safety
const promoToStorageKey = (promo: Promo): StorageKeys => {
  switch (promo) {
    case Promo.FREDDIE_POPUP:
      return StorageKeys.FREDDIE_POPUP;
    case Promo.FREDDIE_HEADER:
      return StorageKeys.FREDDIE_HEADER;
    case Promo.FANNIE_POPUP:
      return StorageKeys.FANNIE_POPUP;
    case Promo.FANNIE_HEADER:
      return StorageKeys.FANNIE_HEADER;
    case Promo.VOA_POPUP:
      return StorageKeys.VOA_POPUP;
    case Promo.VOA_HEADER:
      return StorageKeys.VOA_HEADER;
    case Promo.VOA_FANNIE_POPUP:
      return StorageKeys.VOA_FANNIE_POPUP;
    case Promo.VOA_FANNIE_HEADER:
      return StorageKeys.VOA_FANNIE_HEADER;
    case Promo.SUBSCRIPTION_HEADER:
      return StorageKeys.SUBSCRIPTION_HEADER;
    case Promo.ACCOUNT_ACTIVATION_HEADER:
      return StorageKeys.ACCOUNT_ACTIVATION_HEADER;
  }
};

export type PromoConfig = {
  id: Promo;
  enabled?: boolean;
  interval?: number;
  href?: string;
};

const isPromoActive = (promo: PromoConfig) => {
  if (promo.enabled === false) {
    return false;
  }

  let localPromo = null;

  localPromo = localStorageService.get(promoToStorageKey(promo.id));

  if (localPromo === false) {
    return false;
  }

  const snoozeTime = (promo.interval ?? 0) * 1000;

  if (snoozeTime && localPromo && Date.now() < Number(localPromo) + snoozeTime) {
    return false;
  }

  return true;
};

const setLocalPromo = (id: Promo, value: boolean | string) => {
  localStorageService.set(promoToStorageKey(id), value);
};

const setPromoShown = (id: Promo) => {
  setLocalPromo(id, Date.now().toString());
};

const setPromoClosed = (id: Promo) => {
  setLocalPromo(id, false);
};

const FREDDIE_LINK = 'https://truv.com/freddie-mac-approved';
const FANNIE_LINK = 'https://truv.com/truv-teams-up-with-fannie-mae-to-revolutionize-borrower-verifications';
const VOA_LINK =
  'https://truv.com/blog/truv-is-now-an-authorized-asset-report-supplier-for-fannie-maes-desktop-underwriter-du-validation-service';

export const usePromo = () => {
  const { data: features } = useFeatures();
  const { data: billingProfile } = useGetBillingProfile();
  const { permissions, user } = useAuth();

  const shouldShowSubscriptionBanner = useMemo(() => {
    if (!billingProfile || !features?.flags?.show_subscription_flow || !permissions.has('can_edit_billing')) {
      return false;
    }

    // Only show banner when user can both edit billing AND subscribe
    if (billingProfile.can_subscribe === false) {
      return false;
    }

    return true;
  }, [billingProfile, permissions, features?.flags?.show_subscription_flow]);

  const shouldShowAccountActivationBanner = useMemo(() => {
    if (!billingProfile || !user) {
      return false;
    }

    // Show account activation banner when subscription is active, user doesn't have production key, and it's not a one-off subscription
    return (
      billingProfile.subscription_status === 'active' &&
      user.hasProductionKey === false &&
      billingProfile.subscription_id !== 'one_off'
    );
  }, [billingProfile, user]);

  const subscriptionBannerPromo: PromoConfig | null = useMemo(() => {
    if (!shouldShowSubscriptionBanner) {
      return null;
    }

    return {
      id: Promo.SUBSCRIPTION_HEADER,
      enabled: true,
      href: urls.subscriptionFlow.url(),
    };
  }, [shouldShowSubscriptionBanner]);

  const accountActivationBannerPromo: PromoConfig | null = useMemo(() => {
    if (!shouldShowAccountActivationBanner) {
      return null;
    }

    return {
      id: Promo.ACCOUNT_ACTIVATION_HEADER,
      enabled: true,
    };
  }, [shouldShowAccountActivationBanner]);

  const allPromos = useMemo(() => {
    const featurePromos = features?.promo || [];
    const customPromos = [];

    // Account activation banner has higher priority than subscription banner
    if (accountActivationBannerPromo) {
      customPromos.push(accountActivationBannerPromo);
    } else if (subscriptionBannerPromo) {
      customPromos.push(subscriptionBannerPromo);
    }

    return [...customPromos, ...featurePromos];
  }, [features?.promo, subscriptionBannerPromo, accountActivationBannerPromo]);

  const activePromo = useMemo(() => allPromos?.find((promo) => isPromoActive(promo)), [allPromos]);

  const [showPromoModal, hidePromoModal] = useModal(
    ({ in: open = false }: TransitionProps) => {
      if (activePromo?.id === Promo.FANNIE_POPUP) {
        return (
          <FannieModal
            href={activePromo.href ?? FANNIE_LINK}
            open={open}
            onClick={() => {
              setPromoClosed(Promo.FANNIE_POPUP);
              setPromoClosed(Promo.FANNIE_HEADER);
            }}
            onClose={hidePromoModal}
          />
        );
      }

      if (activePromo?.id === Promo.FREDDIE_POPUP) {
        return (
          <FreddieModal
            href={activePromo.href ?? FREDDIE_LINK}
            open={open}
            onClick={() => {
              setPromoClosed(Promo.FREDDIE_POPUP);
              setPromoClosed(Promo.FREDDIE_HEADER);
            }}
            onClose={hidePromoModal}
          />
        );
      }

      if (activePromo?.id === Promo.VOA_POPUP) {
        return (
          <VOAModal
            href={activePromo.href ?? VOA_LINK}
            open={open}
            onClick={() => {
              hidePromoModal();
              setPromoClosed(Promo.VOA_POPUP);
              setPromoClosed(Promo.VOA_HEADER);
            }}
            onClose={hidePromoModal}
          />
        );
      }

      if (activePromo?.id === Promo.VOA_FANNIE_POPUP) {
        return (
          <VOAModal
            href={activePromo.href ?? VOA_LINK}
            open={open}
            onClick={() => {
              hidePromoModal();

              setPromoClosed(Promo.VOA_FANNIE_POPUP);
              setPromoClosed(Promo.VOA_FANNIE_HEADER);
            }}
            onClose={hidePromoModal}
          />
        );
      }
    },
    [activePromo],
  );

  useEffect(() => {
    if (!activePromo?.id) {
      return;
    }

    showPromoModal();

    if (activePromo.id === Promo.SUBSCRIPTION_HEADER || activePromo.id === Promo.ACCOUNT_ACTIVATION_HEADER) {
      return;
    }

    setPromoShown(activePromo.id);
  }, [showPromoModal, activePromo]);

  let promoHeader = null;

  if (activePromo?.id === Promo.SUBSCRIPTION_HEADER) {
    promoHeader = <SubscriptionHeader href={activePromo?.href ?? urls.subscriptionFlow.url()} />;

    return {
      activePromo,
      promoHeader,
    };
  }

  if (activePromo?.id === Promo.ACCOUNT_ACTIVATION_HEADER) {
    promoHeader = <AccountActivationHeader />;

    return {
      activePromo,
      promoHeader,
    };
  }

  if (activePromo?.id === Promo.FANNIE_HEADER) {
    promoHeader = (
      <FannieHeader
        href={activePromo?.href ?? FANNIE_LINK}
        onClick={() => {
          setPromoClosed(Promo.FANNIE_POPUP);
          setPromoClosed(Promo.FANNIE_HEADER);
        }}
      />
    );
  }

  if (activePromo?.id === Promo.FREDDIE_HEADER) {
    promoHeader = (
      <FreddieHeader
        href={activePromo?.href ?? FREDDIE_LINK}
        onClick={() => {
          setPromoClosed(Promo.FREDDIE_POPUP);
          setPromoClosed(Promo.FREDDIE_HEADER);
        }}
      />
    );
  }

  if (activePromo?.id === Promo.VOA_HEADER) {
    promoHeader = (
      <VOAHeader
        href={activePromo?.href ?? VOA_LINK}
        onClick={() => {
          setPromoClosed(Promo.VOA_HEADER);
          setPromoClosed(Promo.VOA_POPUP);
        }}
      />
    );
  }

  if (activePromo?.id === Promo.VOA_FANNIE_HEADER) {
    promoHeader = (
      <VOAHeader
        href={activePromo?.href ?? VOA_LINK}
        onClick={() => {
          setPromoClosed(Promo.VOA_FANNIE_HEADER);
          setPromoClosed(Promo.VOA_FANNIE_POPUP);
        }}
      />
    );
  }

  return {
    activePromo,
    promoHeader,
  };
};
